import * as moment from 'moment';
import {CHANNEL_NAME, SMS, WHATSAPP} from '../../../../../shared/utils/config';
import {
    ButtonBubble, ButtonBubbleHeader,
    Contact,
    ContentInteractive,
    ContentLocation,
    ContentMedia, LocationRequestBubble,
    metaData,
    OutboundTranslator,
    TranslationResponse
} from './translatorUtils';
import {EmulatorMessage} from '../../../../../shared/models';

interface MediaFile {
    contentType: string;
    downloadUrl: string;
    sha256Hash: string;
    byteSize: number;
    fileName: string;
}

interface SelectedItem {
    postbackData: string;
    content?: string;
}

interface OneApiResponse {
    integrationId: string;
    integrationName: string;
    event: {};
}

interface MoText {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    content: string;
    charset: string;
    whatsapp: any;
}

interface MoMedia {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    caption: string;
    whatsapp: any;
    files: MediaFile[];
}

interface MoLocation {
    whatsapp: any;
    charset: string;
    locationName: any;
    latitude: any;
    channel: string;
    messageId: string;
    locationAddress: any;
    relatedMessageId: any;
    from: string;
    to: string;
    relatedClientMessageId: null;
    timestamp: number;
    longitude: any;
}

interface MoList {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    postbackData: string;
    whatsapp: any;
    selectedItems: [SelectedItem];
}

interface MoButton {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    postbackData: string;
    whatsapp: any;
    selectedItem: SelectedItem;
}

interface MoContacts {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    whatsapp: any;
    contacts: any[];
}

export class OneApiTranslatorInbound {

    private static messagePayload(message: any, emulatorMessage: EmulatorMessage): EmulatorMessage {
        if (message['media']) {
            return OneApiTranslatorInbound.media(emulatorMessage, message);
        }
        if (message['location']) {
            return OneApiTranslatorInbound.location(emulatorMessage, message);
        }
        if (message['button']) {
            return OneApiTranslatorInbound.button(emulatorMessage, message);
        }
        if (message['listPicker']) {
            return OneApiTranslatorInbound.listPicker(emulatorMessage);
        }
        if (message['contacts']) {
            return OneApiTranslatorInbound.contacts(emulatorMessage, message);
        }
        if (message['locationRequest']) {
            return OneApiTranslatorInbound.locationRequest(emulatorMessage, message);
        }
        if (message['actionUrl']) {
            return OneApiTranslatorInbound.callToAction(emulatorMessage, message);
        }
        return OneApiTranslatorInbound.text(emulatorMessage);
    }

    static eventDelegator(emulatorMessage: EmulatorMessage): EmulatorMessage {
        const payload = emulatorMessage['channelPayload'];
        if ('messages' in payload) {
            const message = payload['messages'][0];
            return OneApiTranslatorInbound.messagePayload(message, emulatorMessage);
        }
        if ('event' in payload) {
            return OneApiTranslatorInbound.eventPayload(emulatorMessage, payload);
        }

        return OneApiTranslatorInbound.text(emulatorMessage);
    }

    private static eventPayload(emulatorMessage: EmulatorMessage, payload): EmulatorMessage {
        const event = payload['event'];

        if ('messageStatusUpdate' in event) {
            return OneApiTranslatorInbound.messageStatusEvent(emulatorMessage, event);
        }

        throw Error('Event type not supported');
    }

    private static messageStatusEvent(emulatorMessage: EmulatorMessage, event): EmulatorMessage {

        const messageStatusUpdate = event['messageStatusUpdate'][0];
        const status = messageStatusUpdate['statusCode'];

        let statusCode = 'error';

        switch (status) {
            case 4: // SENT_TO_SUPPLIER
                statusCode = 'send';
                break;
            case 5: // DEVICE_ACK
                statusCode = 'deliver';
                break;
            case 1001: // READ
                statusCode = 'read';
                break;
        }

        emulatorMessage.mediaMetaData = {
            src: '',
            status: statusCode,
            messageId: messageStatusUpdate['messageId'],
            bubbleType: 'notification',
        };
        return emulatorMessage;
    }


    private static media(emulatorMessage: EmulatorMessage, message: any): EmulatorMessage {
        const ref = message['media']['fileId'];
        const fileMetaData = emulatorMessage['channelPayload']['emulatorFiles'][ref];
        const type = fileMetaData['type'].split('/')[0];
        switch (type) {
            case 'image':
                return OneApiTranslatorInbound.image(emulatorMessage, fileMetaData);
            case 'video':
                return OneApiTranslatorInbound.video(emulatorMessage, fileMetaData);
            case 'application':
                return OneApiTranslatorInbound.application(emulatorMessage, fileMetaData);
            case 'audio':
                return OneApiTranslatorInbound.audio(emulatorMessage, fileMetaData);
        }
        return emulatorMessage;
    }


    private static image(emulatorMessage: EmulatorMessage, fileMetaData: any): EmulatorMessage {
        let description = '';
        if (emulatorMessage['channelPayload']['messages'][0]['media'] && emulatorMessage['channelPayload']['messages'][0]['media']['caption']) {
            description = emulatorMessage['channelPayload']['messages'][0]['media']['caption'];
        }
        emulatorMessage.mediaMetaData = {
            src: fileMetaData['src'],
            description: description,
            bubbleType: 'image',
        };
        return emulatorMessage;
    }

    private static video(emulatorMessage: EmulatorMessage, fileMetaData: any): EmulatorMessage {
        let description = '';

        if (emulatorMessage['channelPayload']['messages'][0]['media'] &&
            emulatorMessage['channelPayload']['messages'][0]['media']['caption']) {
            description = emulatorMessage['channelPayload']['messages'][0]['media']['caption'];
        }

        emulatorMessage.mediaMetaData = {
            src: fileMetaData['src'],
            type: fileMetaData['type'],
            bubbleType: 'video',
            description: description
        };
        return emulatorMessage;
    }

    private static application(emulatorMessage: EmulatorMessage, fileMetaData: any): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            type: fileMetaData['type'],
            src: fileMetaData['src'],
            description: fileMetaData['name'],
            fileSize: fileMetaData['size'],
            bubbleType: 'file',
        };
        return emulatorMessage;
    }

    private static audio(emulatorMessage: EmulatorMessage, fileMetaData: any): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            src: fileMetaData['src'],
            bubbleType: 'voicenote',
        };
        return emulatorMessage;
    }


    private static location(emulatorMessage: EmulatorMessage, message: any): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            locationName: message['location'].name,
            locationAddress: message['location'].address,
            latitude: message['location'].latitude,
            longitude: message['location'].longitude,
            src: '',
            bubbleType: 'location',
        };
        return emulatorMessage;
    }

    private static button(emulatorMessage: EmulatorMessage, message: any): EmulatorMessage {
        const channelPayloadButton = message['button'];

        const buttonBubble: ButtonBubble = {
            items: channelPayloadButton['items'] || [],
            body: channelPayloadButton['body'] || undefined,
            footer: channelPayloadButton['footer'] || undefined,
        };

        if (channelPayloadButton['header'] && message['button']['header']['media']) {
            const ref = message['button']['header']['media']['fileId'];
            const fileMetaData = emulatorMessage['channelPayload']['emulatorFiles'][ref];
            const type = fileMetaData['type'].split('/')[0];
            switch (type) {
                case 'image':
                    const image = OneApiTranslatorInbound.image(emulatorMessage, fileMetaData) as any;
                    buttonBubble.media = image.mediaMetaData;
                    break;
                case 'video':
                    const video = OneApiTranslatorInbound.video(emulatorMessage, fileMetaData) as any;
                    buttonBubble.media = video.mediaMetaData;
                    break;
                case 'application':
                    const application = OneApiTranslatorInbound.application(emulatorMessage, fileMetaData) as any;
                    buttonBubble.media = application.mediaMetaData;
                    break;
            }
        } else if (channelPayloadButton['header']) {
            buttonBubble.text = channelPayloadButton['header']['text'];
        }

        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'button',
            buttons: buttonBubble
        };

        return emulatorMessage;
    }

    private static listPicker(emulatorMessage: EmulatorMessage): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'list',
        };
        return emulatorMessage;
    }

    private static locationRequest(emulatorMessage: EmulatorMessage, message: any): EmulatorMessage {
        const locationRequest = message['locationRequest'];
        const locationRequestBubble: LocationRequestBubble = {
            text: undefined,
            body: locationRequest.body || undefined,
            src: undefined
        }

        if (locationRequest['header'] && locationRequest['header']['type'] === 'text') {
            locationRequestBubble.text = locationRequest['header']['text'];
        }

        const fileId = locationRequest?.header?.media?.fileId;
        if (locationRequest['header'] && locationRequest['header']['type'] === 'media' && fileId) {
            locationRequestBubble.src = emulatorMessage['channelPayload']['emulatorFiles'][fileId].src;
        }

        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'requestlocation',
            locationRequest: locationRequestBubble
        };
        return emulatorMessage;
    }

    private static callToAction(emulatorMessage: EmulatorMessage, message: any): EmulatorMessage {
        const actionUrl = message['actionUrl'];
        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'actionUrl',
            actionUrl: actionUrl
        };
        return emulatorMessage;
    }


    private static text(emulatorMessage: EmulatorMessage): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'text',
            content: emulatorMessage['channelPayload']['messages'][0]['content'],
        };
        return emulatorMessage;
    }

    private static contacts(emulatorMessage: EmulatorMessage, message: any): EmulatorMessage {
        const contact = message['contacts'][0];
        emulatorMessage.mediaMetaData = {
            src: '',
            messageId: '',
            bubbleType: 'contact',
            contacts: contact
        };
        return emulatorMessage;
    }
}

class OneApiTranslatorOutbound {
    translate: OutboundTranslator;

    oneApiResponse: OneApiResponse = {
        integrationId: '',
        integrationName: '',
        event: null,
    };

    messageId = 'test-message-' + Math.floor(Math.random() * 100000);

    constructor(translate: OutboundTranslator) {
        this.translate = translate;
        this.oneApiResponse.integrationId = translate.integrationId;
        this.oneApiResponse.integrationName = 'Test Integration';
        if (translate.triggerContactBookFlow) {
            (this.oneApiResponse as any).triggerContactBookFlow = true;
        }
    }

    get media() {
        const content: ContentMedia = this.translate.content as ContentMedia;
        const moMedia: MoMedia = {
            channel: CHANNEL_NAME[WHATSAPP],
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: null,
            from: this.translate.to,
            to: '2799900001',
            timestamp: moment.now(),
            files: [
                {
                    contentType: content.contentType,
                    downloadUrl: content.downloadUrl,
                    sha256Hash: content.sha256Hash,
                    byteSize: content.byteSize,
                    fileName: content.fileName,
                },
            ],
            caption: content.caption,
            whatsapp: {
                profileName: 'Profile Name',
            },
        };

        this.event = {moMedia: [moMedia]};
        return this.oneApiResponse;
    }

    get list() {
        const content: ContentInteractive = this.translate.content as ContentInteractive;
        const moListPickerResponse: MoList = {
            channel: CHANNEL_NAME[WHATSAPP],
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: content.relatedClientMessageId,
            from: this.translate.to,
            to: '2799900001',
            timestamp: moment.now(),
            postbackData: null,
            selectedItems: [{
                postbackData: content.postbackData,
                content: content.messageDisplay
            }],
            whatsapp: {
                profileName: 'Profile Name',
            },
        };

        this.event = {moListPickerResponse: [moListPickerResponse]};
        return this.oneApiResponse;
    }

    get button() {
        const content: ContentInteractive = this.translate.content as ContentInteractive;

        const moButton: MoButton = {
            channel: CHANNEL_NAME[WHATSAPP],
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: content.relatedClientMessageId,
            from: this.translate.to,
            to: '2799900001',
            timestamp: moment.now(),
            postbackData: null,
            selectedItem: {
                postbackData: content.postbackData,
                content: content.messageDisplay
            },
            whatsapp: {
                profileName: 'Profile Name',
            },
        };

        this.event = {moButtonResponse: [moButton]};
        return this.oneApiResponse;
    }

    get location() {
        const content: ContentLocation = this.translate.content as ContentLocation;
        const moLocation: MoLocation = {
            channel: CHANNEL_NAME[WHATSAPP],
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: null,
            from: this.translate.to,
            to: '2799900001',
            timestamp: moment.now(),
            charset: 'UTF-8',
            locationAddress: content.locationAddress,
            locationName: content.locationName,
            latitude: content.latitude,
            longitude: content.longitude,
            whatsapp: {
                profileName: 'Profile Name',
            },
        };
        this.event = {moLocation: [moLocation]};
        return this.oneApiResponse;
    }

    getText(channel: string) {
        const moText: MoText = {
            channel: channel,
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: null,
            from: this.translate.to,
            to: '2799900001',
            timestamp: moment.now(),
            content: this.translate.content as string,
            charset: 'UTF-8',
            whatsapp: {
                profileName: 'Profile Name',
                ...(this.translate.isBsuid ? { userId: this.translate.to } : {}),
            },
        };

        this.event = {moText: [moText]};
        return this.oneApiResponse;
    }


    get contact() {
        const contact: Contact = this.translate.content as Contact;

        const moContacts: MoContacts = {
            channel: CHANNEL_NAME[WHATSAPP],
            messageId: this.messageId,
            relatedMessageId: null,
            from: this.translate.to,
            to: '2799900001',
            timestamp: moment.now(),
            contacts: [{
                addresses: contact.addresses,
                birthday: contact.birthday,
                emails: contact.emails,
                name: contact.name,
                org: contact.org,
                phones: contact.phones,
                urls: contact.urls
            }],
            whatsapp: {
                profileName: 'Profile Name',
            },
            relatedClientMessageId: null
        };

        this.event = {moContacts: [moContacts]};
        return this.oneApiResponse;
    }


    private set event(moEvent: {}) {
        this.oneApiResponse.event = moEvent;
    }
}

const translateToOneApi = (translate: OutboundTranslator): TranslationResponse => {
    const oneApi = new OneApiTranslatorOutbound(translate);
    const translationResponse: TranslationResponse = {
        channelPayload: {},
        mediaData: {
            src: '',
            messageId: 'test-message-' + Math.floor(Math.random() * 100000),
            bubbleType: 'text',
        },
    };

    const channelId = translate.channelId;

    if (channelId === SMS) {
        if (translate.type === 'media' || translate.type === 'location') {
            console.error('media and location is not supported for SMS');
            return;
        }
        translationResponse.channelPayload = oneApi.getText(CHANNEL_NAME[channelId]);
        translationResponse.mediaData.messageId = oneApi.messageId;
        return translationResponse;
    }

    switch (translate.type) {
        case 'location':
            translationResponse.channelPayload = oneApi.location;
            translationResponse.mediaData = metaData(translate.content as ContentLocation, oneApi.messageId);
            break;
        case 'media':
            translationResponse.channelPayload = oneApi.media;
            translationResponse.mediaData = metaData(translate.content as ContentMedia, oneApi.messageId);
            break;
        case 'button':
            translationResponse.channelPayload = oneApi.button;
            translationResponse.mediaData = metaData(translate.content as ContentInteractive, oneApi.messageId);
            break;
        case 'list':
            translationResponse.channelPayload = oneApi.list;
            translationResponse.mediaData = metaData(translate.content as ContentInteractive, oneApi.messageId);
            break;
        case 'contact':
            translationResponse.channelPayload = oneApi.contact;
            translationResponse.mediaData = metaData(translate.content as Contact, oneApi.messageId);
            break;
        default:
            translationResponse.channelPayload = oneApi.getText(CHANNEL_NAME[channelId]);
            translationResponse.mediaData.messageId = oneApi.messageId;
            translationResponse.mediaData.content = translate.content as string;
            break;
    }

    return translationResponse;
};

export default translateToOneApi;

